import React from 'react';
import DropdownRenderer from './DropdownRenderer';
import CheckboxRenderer from './CheckboxRenderer';
import CheckboxListRenderer from './CheckboxListRenderer';
import TextareaRenderer from './TextareaRenderer';

/**
 * RiskAssessmentGroup - Renders Risk Assessment fields with quick-fill button
 * and conditional rendering of follow-up questions based on risk presence
 */
const RiskAssessmentGroup = ({ fields, formData, onChange, errors, readOnly }) => {
  const handleQuickFillNoRisk = () => {
    if (readOnly) return;

    const updates = {};
    
    const noRiskMappings = {
      'risk_areas': ['None identified'],
      'area_of_risk': ['None identified'],
      'suicidal_ideation': 'Denies suicidal ideation',
      'homicidal_ideation': 'Denies homicidal ideation',
      'overall_risk_level': 'Low risk',
      'safety_interventions': ['Continue outpatient treatment as planned'],
      'interventions': ['Continue outpatient treatment as planned'],
      'safety_plan_status': 'Safety plan not indicated',
    };
    
    fields.forEach(field => {
      const noRiskValue = noRiskMappings[field.field_id];
      if (noRiskValue !== undefined) {
        updates[field.field_id] = noRiskValue;
      }
    });

    // Batch all updates in a single call to avoid render cycle issues
    // with multiple rapid setFormData calls
    onChange(null, updates);
  };

  const hasRiskIdentified = () => {
    const riskAreasField = fields.find(f => f.field_id === 'risk_areas' || f.field_id === 'area_of_risk');
    if (!riskAreasField) return false;
    
    const riskAreas = formData[riskAreasField.field_id];
    if (!riskAreas) return false;
    
    if (Array.isArray(riskAreas)) {
      return riskAreas.length > 0 && !riskAreas.every(area => 
        area === 'None identified' || area === 'none identified'
      );
    }
    
    return false;
  };

  const shouldShowFollowUpField = (field) => {
    const primaryFields = ['risk_areas', 'area_of_risk', 'suicidal_ideation', 'homicidal_ideation', 'overall_risk_level'];
    if (primaryFields.includes(field.field_id)) {
      return true;
    }

    return hasRiskIdentified();
  };

  const renderField = (field) => {
    const commonProps = {
      field,
      value: formData[field.field_id] || field.default_value || '',
      onChange: (value) => onChange(field.field_id, value),
      error: errors[field.field_id],
      readOnly,
    };

    switch (field.type) {
      case 'multiselect':
      case 'multiselect_with_freetext':
        // Use CheckboxListRenderer for better UX - allows clicking to toggle
        // individual items without needing Ctrl/Cmd+click
        return <CheckboxListRenderer key={field.field_id} {...commonProps} />;
      case 'dropdown':
      case 'dropdown_with_freetext':
        return <DropdownRenderer key={field.field_id} {...commonProps} />;
      case 'textarea':
        return <TextareaRenderer key={field.field_id} {...commonProps} />;
      case 'checkbox':
        return <CheckboxRenderer key={field.field_id} {...commonProps} />;
      default:
        return <DropdownRenderer key={field.field_id} {...commonProps} />;
    }
  };

  return (
    <div className="border border-gray-300 rounded-lg p-4 mb-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Risk Assessment</h3>
        {!readOnly && (
          <button
            type="button"
            onClick={handleQuickFillNoRisk}
            className="px-4 py-1.5 text-sm font-medium bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
            title="Deny all risk — sets all fields to safe defaults in one click"
          >
            Deny Risk
          </button>
        )}
      </div>

      <div className="space-y-3">
        {fields.map(field => {
          if (!shouldShowFollowUpField(field)) {
            return null;
          }
          return (
            <div key={field.field_id}>
              {renderField(field)}
            </div>
          );
        })}
      </div>

      {!hasRiskIdentified() && (
        <p className="text-sm text-gray-500 mt-3 italic">
          Additional risk assessment fields will appear if risk is identified.
        </p>
      )}
    </div>
  );
};

export default RiskAssessmentGroup;
